import { defineStore } from 'pinia';

import type {
  CartApiItem,
  CartApiPayload,
  CartApiResponse,
  CheckoutAddressDetails,
  CheckoutApiResponse,
  CheckoutDeliveryMethod,
  CheckoutPaymentMethod,
  OrderProduct,
  OrderProposal,
  PurchaseResponse,
  UserProfile,
  UserProfileAddress
} from '#constants/types/norce';
import { useUiStore } from '~/store/ui';
import useApiFetch from '~/composables/useApiFetch';
import { createDataLayerItem, sendDataLayer } from '~/composables/useTracking';
import type { ProductVariantModel } from '#models/productVariant';
import { useUserStore } from '~/store/user';
import { NorceInfoTypesCodes, NorceBasketTypes } from '#constants/norceCodes';

import aa from 'search-insights';
import type { ProductModel } from '#models/product';
import useLinkReplacer from '~/composables/useLinkReplacer';
import * as Sentry from '@sentry/nuxt';
import { useGlobalContentStore } from '~/store/globalContent';
import { hasNorceCheckoutSelected } from '~/composables/norce-checkout';

const emptyCart = {
  id: '',
  norceBasketId: '',
  items: [],
  summary: {
    discount: '',
    discountDisplay: '',
    points: -1,
    price: '',
    priceBeforeVat: '',
    priceBeforeDiscount: '',
    priceBeforeVatDisplay: '',
    priceBeforeDiscountDisplay: '',
    priceDisplay: '',
    shipping: '',
    shippingBeforeVat: '',
    shippingBeforeVatDisplay: '',
    shippingDisplay: '',
    subtotal: '',
    subtotalBeforeVat: '',
    subtotalBeforeVatDisplay: '',
    subtotalDisplay: '',
    vat: '',
    vatDisplay: '',
    usedPoints: -1,
    crossDockingShipping: '',
    crossDockingShippingDisplay: '',
    crossDockingShippingBeforeVat: '',
    crossDockingShippingBeforeVatDisplay: '',
    additionalFees: undefined,
  },
} as CartApiResponse;

export const customerSuccessItemFilter = (item: CartApiItem | OrderProduct) => {
  const globalContent = useGlobalContentStore();
  const feePartNos = [
    '30000',
    '30001',
    globalContent.consumerFeePartNo,
    globalContent.outsideModDeliveryFeePartNo,
    globalContent.tailLiftPartNo,
    globalContent.timedUnloadingPartNo,
  ].filter((partNo): partNo is string => Boolean(partNo));

  return !feePartNos.includes(item.partNo);
};

type multiAddToCart = {
  partNo: string;
  quantity: number;
  product?: CartApiItem | ProductVariantModel | OrderProduct | null
}

export type UpdateWithLineNo = {partNo: string, quantity: number, lineNo: number}
type DiscountUpdateItem = {
  lineNo: number;
  discount: string;
  quantity: number;
  priceListLocked?: boolean;
  discountType?: 'percentage' | 'fixedPerM2';
  shouldUpdateProduct?: boolean;
  shouldUpdateExistingLineNo?: boolean;
};

export const useCartStore = defineStore('cartStore', {
  state: () => ({
    cartId: '',
    cartItems: 0, // save separately, easier to watch
    cart: emptyCart as CartApiResponse,
    isLoading: false, // for standard cart
    alternativeCart: emptyCart as CartApiResponse, // load another cart than standard, used for pointShop and checkout finished
    isAlternativeLoading: false,
    currentlyBuying: null as ProductModel | null,
    currentlyBuyingPrimaryVariant: true,
    isBuyingUpsellProduct: false,
    currentlyBuyingQuantity: 0,
    justAddedPartNo: '',
    lastUpdate: 0,
    shippingAddress: {
      companyName: '',
      firstName: '',
      lastName: '',
      street: '',
      zip: '',
      city: '',
      country: '',
      phone: '',
      email: '',
      id: null,
      state: null,
    } as CheckoutAddressDetails,
    billingAddress: {
      companyName: '',
      firstName: '',
      lastName: '',
      street: '',
      zip: '',
      city: '',
      country: '',
      phone: '',
      email: '',
      id: null,
    } as CheckoutAddressDetails,
    payerAddress: {
      companyName: '',
      firstName: '',
      lastName: '',
      street: '',
      zip: '',
      city: '',
      country: '',
      phone: '',
      email: '',
      id: null,
    } as CheckoutAddressDetails,
    deliveryMethods: [] as CheckoutDeliveryMethod[],
    paymentMethods: [] as CheckoutPaymentMethod[],
    buyerCompanyName: null as string|null,
    loadingCart: true,
    loadingCheckout: true,
    loadingPurchase: false,
    hasAttemptedPurchase: false,
    acceptedNewsletter: false,
    pointsUsed: 0,
    pointsAvailable: -1,
    addedToCartCount: 0,
    deboucedAddToCart: [] as multiAddToCart[],
    deboucedTimer: null as ReturnType<typeof setTimeout> | null,
    checkoutVersion: 0,
    toggleModFeeInProgress: false,
  }),
  getters: {
    items(state) {
      return state.cart?.items ?? [];
    },
    // returns number of products bought by points
    pointStoreItemsInCart(state) {
      return state.alternativeCart.items.filter((f) => {
        const infoType = f.infoTypes.findIndex((f2) => f2.name === NorceInfoTypesCodes.PointShopValue);
        return infoType > -1;
      }).reduce((a, c) => a + c.quantity, 0);
    },
    // returns the item in alternative cart that is bought from pointstore
    pointStoreItems(state) {
      return state.alternativeCart.items.filter((f) => f.infoTypes.some((s) => s.name === NorceInfoTypesCodes.PointShopValue));
    },
    hasDiscount(state) {
      return state.cart?.summary?.discount && state.cart?.summary?.discount !== '0';
    },
    /*
      Check if cart has actual discounts (sales rep, campaign) not just SRP difference
      Used when hideSRP is enabled to only show discount summary for real discounts
     */
    hasActualDiscount(state) {
      const globalStore = useGlobalContentStore();
      if (globalStore.getIsB2c) {
        // On B2C, only show discount summary when items are on a campaign price
        return state.cart?.items?.some(item =>
          item.isD2C && item.priceInfoTotal.isCampaignPrice
        ) ?? false;
      }
      if (!globalStore.hideSRP) {
        // If not hiding SRP, use the standard hasDiscount
        return state.cart?.summary?.discount && state.cart?.summary?.discount !== '0';
      }
      // When hiding SRP, check if any items have actual discounts
      return state.cart?.items?.some(item =>
        (item.priceInfoTotal.discountPercentage ?? 0) > 0 ||
        item.priceInfoTotal.isCampaignPrice
      ) ?? false;
    },
    appliedDiscountNames(state): string {
      return state.cart?.appliedPromotions?.map(ap => ap.discountCode)?.join(',') ?? '';
    },
    hasSelectedPaymentMethod(state): boolean {
      return state.cart?.paymentInfo?.hasSelectedMethod ?? false;
    },
    getPreferredDeliveryDates(state) {
      if (state.cart?.infoTypes?.filter((i) => i.name === NorceInfoTypesCodes.MergeShipments && i.value === 'true')?.length) {
        return [];
      }
      const globalContent = useGlobalContentStore();
      const feePartNos = [
        globalContent.consumerFeePartNo,
        globalContent.palletDiscountPartNo,
        globalContent.outsideModDeliveryFeePartNo,
        globalContent.tailLiftPartNo,
        globalContent.timedUnloadingPartNo,
      ].filter((partNo): partNo is string => Boolean(partNo));
      const map: string[] = [];
      state.cart.items.forEach((item) => {
        if (feePartNos.includes(item.partNo)) {
          return;
        }
        const date = item.infoTypes.filter((i) => i.name === NorceInfoTypesCodes.PreferredShippingDate && i.value !== '')[0]?.value ?? null;
        if (date && !map.includes(date)) {
          map.push(date);
        }
      });
      return map.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    },
    hasMultiplePreferredDeliveryDates(): boolean {
      return this.getPreferredDeliveryDates.length > 1;
    },
    isNetsEasyCheckout(state): boolean {
      return state.paymentMethods.find(method => method.selected)?.id === 41 || false;
    },
    isNorceCheckoutV2(state): boolean {
      return hasNorceCheckoutSelected(state.paymentMethods);
    },
    isOrderProposalBasket(state): boolean {
      return state.cart?.infoTypes?.find(i => i.name === NorceInfoTypesCodes.BasketType)?.value === NorceBasketTypes.OrderProposal || false;
    },
    getCopiedFromOrderId(state): string | null {
      return state.cart?.infoTypes?.find(i => i.name === NorceInfoTypesCodes.CopiedFrom)?.value ?? null;
    },
    mayAddMoreForPartNo: (state) => (partNo: string) => {
      const findLineNo = state.cart.items.find((f) => f.partNo === partNo);
      if (findLineNo) {
        const today = new Date().toISOString().split('T')[0];
        if (findLineNo.stockStatus.estimatedDelivery && findLineNo.stockStatus.estimatedDelivery > today) {
          return true;
        }
        return findLineNo.stockStatus.value > findLineNo.quantity;
      }

      return true;
    },
    deliveryLeadTime: (state) => {
      const itemWithLongestLeadtime = state.cart?.items.reduce((prev: CartApiItem | null, current: CartApiItem) => {
        const currentLeadTime = current.stockStatus.leadtimeDayCount;

        if (currentLeadTime === undefined) {
          return prev;
        }

        if (prev === null || (currentLeadTime !== null && currentLeadTime > prev.stockStatus.leadtimeDayCount!)) {
          return current;
        }

        return prev;
      }, null);

      return itemWithLongestLeadtime?.stockStatus?.leadtimeDayCount || 1;
    },
    getQuantityFromPartNo: (state) => (partNo: string, priceListLocked = false): number => {
      const find = state.cart.items.find((f) => {
        if (priceListLocked) {
          return f.partNo === partNo && f.priceListLocked === priceListLocked;
        }
        return f.partNo === partNo;
      });
      if (find) {
        return find.quantity;
      }
      return 0;
    },
    getQuantityFromLineNo: (state) => (lineNo: number) => {
      const find = state.cart.items.find(f => f.lineNo === lineNo);
      if (find) {
        return find.quantity;
      }
      return 0;
    },

    getLineNoFromPartNo: (state) => (partNo: string): number => {
      const find = state.cart.items.find((f) => f.partNo === partNo);
      if (find) {
        return find.lineNo;
      }
      return -1;
    },
    getInvoiceNotice(state): string {
      return state.cart?.infoTypes?.find(i => i.name === NorceInfoTypesCodes.InvoiceNotice)?.value ?? '';
    },
    consumerFeeProduct: (state) => {
      const globalContent = useGlobalContentStore();
      return state.cart.items.find(i => i.partNo === globalContent.consumerFeePartNo);
    },
    hasConsumerFeeProduct: (state) => {
      const globalContent = useGlobalContentStore();
      const find = state.cart.items.find(i => i.partNo === globalContent.consumerFeePartNo);
      return !!find;
    },

    cartTotalArea(): number {
      let totalArea = 0;

      this.cart.items.forEach((item: CartApiItem) => {
        const areaPerPackage = item.infoTypes?.find(info =>
          info.name === 'UnitConversionMetric' || info.name === 'UnitConversionImperial'
        )?.value;

        if (areaPerPackage) {
          totalArea += Number(areaPerPackage) * item.quantity;
        }
      });

      return totalArea;
    },
    cartTotalFloorArea(): number {
      let totalArea = 0;

      this.cart.items.forEach((item: CartApiItem) => {
        const isFloor = item.categories?.some(c => c.code?.toLowerCase() === 'floors');

        if (!isFloor) {
          return;
        }

        const areaPerPackage = item.infoTypes?.find(info =>
          info.name === 'UnitConversionMetric' || info.name === 'UnitConversionImperial'
        )?.value;

        if (areaPerPackage) {
          totalArea += Number(areaPerPackage) * item.quantity;
        }
      });

      return totalArea;
    },
    shouldShowOrderProposalRequest(): boolean {
      const globalContent = useGlobalContentStore();
      return this.cartTotalArea >= globalContent.orderProposalRequestAreaThreshold;
    },
    palletDiscountProduct: (state) => {
      const globalContent = useGlobalContentStore();
      return state.cart.items.find(i => i.partNo === globalContent.palletDiscountPartNo);
    },

    hasPalletDiscountProduct: (state) => {
      const globalContent = useGlobalContentStore();
      const find = state.cart.items.find(i => i.partNo === globalContent.palletDiscountPartNo);
      return !!find;
    },
    hasAccessories: (state) => {
      return state.cart.items.some(i => i.categories.some(c => c.code === 'Accessories'));
    },
    hasSamples: (state) => {
      const globalContent = useGlobalContentStore();
      // Filter out fee items (consumer fee, outside mod fee, etc.)
      const feePartNos = [
        globalContent.consumerFeePartNo,
        globalContent.outsideModDeliveryFeePartNo,
        globalContent.tailLiftPartNo,
        globalContent.timedUnloadingPartNo,
        globalContent.palletDiscountPartNo,
      ].filter((partNo): partNo is string => Boolean(partNo));

      const actualProducts = state.cart.items.filter(item => !feePartNos.includes(item.partNo));

      // Check if at least one product is a sample
      return actualProducts.some(item => item.categories.some(c => c.code === 'Samples'));
    },
    hasOnlySamples: (state) => {
      const globalContent = useGlobalContentStore();
      // Filter out fee items (consumer fee, outside mod fee, etc.)
      const feePartNos = [
        globalContent.consumerFeePartNo,
        globalContent.outsideModDeliveryFeePartNo,
        globalContent.tailLiftPartNo,
        globalContent.timedUnloadingPartNo,
        globalContent.palletDiscountPartNo,
      ].filter((partNo): partNo is string => Boolean(partNo));

      const actualProducts = state.cart.items.filter(item => !feePartNos.includes(item.partNo));

      if (actualProducts.length === 0) {
        return false;
      }

      return actualProducts.every(item => item.categories.some(c => c.code === 'Samples'));
    },
    hasItemInCartOutsideMod(state) {
      const userStore = useUserStore();
      const globalContent = useGlobalContentStore();

      // Exclude fee products from the check
      const feePartNos = [
        globalContent.consumerFeePartNo,
        globalContent.outsideModDeliveryFeePartNo,
      ].filter(Boolean);

      let disabledDays = [6,0];
      const mod = userStore.userProfile.company.mod ?? '';
      if (mod !== '') {
        const allWeekdays = [0,1,2,3,4,5,6];
        const matches = [...mod.matchAll(/[1-7]/g)].map((i) => Number(i[0]));
        disabledDays = allWeekdays.filter(x => !matches.includes(x));
      }
      return state.cart.items.some(i => {
        // Skip fee products
        if (feePartNos.includes(i.partNo)) {
          return false;
        }
        // Use PreferredShippingDate if set, otherwise fall back to DefaultPreferredShippingDate
        // (the first available inside MoD date), and only use today as last resort
        const preferredDate = i.infoTypes?.find(it => it.name === NorceInfoTypesCodes.PreferredShippingDate)?.value;
        const defaultDate = i.infoTypes?.find(it => it.name === NorceInfoTypesCodes.DefaultPreferredShippingDate)?.value;
        const date = new Date(preferredDate || defaultDate || (new Date().toISOString().split('T')[0]));
        return disabledDays.includes(date.getDay());
      });
    },
    hasOutsideModProduct: (state) => {
      const globalContent = useGlobalContentStore();
      const find = state.cart.items.find(i => i.partNo === globalContent.outsideModDeliveryFeePartNo);
      return !!find;
    },
    hasDisabledOutsideModFee(state) {
      const userStore = useUserStore();
      if (!(userStore.isSalesRepUser || userStore.isCustomerSuccessUser)) {
        return false;
      }
      return state.cart.infoTypes?.some(it => it.name === NorceInfoTypesCodes.RemoveModBreakCost && it.value === 'true');
    },
    /**
     * Calculate total eco-tax for all cart items (France only)
     * Formula: sum of (area_per_package * quantity * eco_tax_rate) for each item
     */
    totalEcoTax(state): number {
      let total = 0;
      for (const item of state.cart.items) {
        const ecoTaxValue = parseFloat(item.infoTypes?.find(it => it.name === 'EcoTax')?.value ?? '0');
        const areaPerPackage = parseFloat(item.infoTypes?.find(it => it.name === 'UnitConversionMetric')?.value ?? '0');

        if (ecoTaxValue > 0 && areaPerPackage > 0) {
          total += areaPerPackage * item.quantity * ecoTaxValue;
        }
      }
      return Math.round(total * 10000) / 10000; // Round to 4 decimals
    },
    selectedDeliveryType(state) {
      return state.cart.infoTypes?.find(it => it.name === NorceInfoTypesCodes.DestinationType)?.value ?? '';
    },
    deliveryToAddress(state) {
      return (state.cart.infoTypes?.find(it => it.name === NorceInfoTypesCodes.DestinationType)?.value ?? '') === 'address';
    },
    deliveryToStore(state) {
      const shipToConsumer = state.cart.infoTypes?.find(it => it.name === NorceInfoTypesCodes.ShipToConsumer)?.value === 'true';
      if (shipToConsumer) {
        return false;
      }
      const value = (state.cart.infoTypes?.find(it => it.name === NorceInfoTypesCodes.DestinationType)?.value ?? 'store');
      return value === 'store' || value === '';
    },
    deliveryToConsumer(state) {
      // Check both DestinationType (checkout v2) and ShipToConsumer (checkout v1)
      const destinationType = state.cart.infoTypes?.find(it => it.name === NorceInfoTypesCodes.DestinationType)?.value ?? '';
      const shipToConsumer = state.cart.infoTypes?.find(it => it.name === NorceInfoTypesCodes.ShipToConsumer)?.value === 'true';
      return destinationType === 'consumer' || shipToConsumer;
    },
  },
  actions: {
    async loadAlternativeBasket(basketId: string) {
      const { apiGet, handleApiError } = useApiFetch();
      const res = await apiGet<CartApiResponse>(`/basket/${basketId}?storeBasket=0`, null, false);
      if (res) {
        if (res.allowPointsPurchase) {
          this.pointsAvailable = res.summary.points;
          this.pointsUsed = res.summary.usedPoints;
          if (this.pointsUsed > 0) {
            console.log('this.pointsUsed > 0 - removes cookie');
            // removes reminder cookie, if shopping started
            const pointShopReminder = useCookie('pointShopReminder');
            pointShopReminder.value = null;
          }
        }
        this.alternativeCart = res;
        return true;

      } else {
        handleApiError();
        return false;
      }
    },
    // this adds/subtract
    changePointsUsed(payload: number) {
      this.pointsUsed += payload;
    },
    setCurrentlyBuying(payload: ProductModel, isUpsell?: boolean, usePrimaryVariant?: boolean) {
      this.currentlyBuying = payload;
      this.currentlyBuyingPrimaryVariant = usePrimaryVariant ?? true;
      this.isBuyingUpsellProduct = !!isUpsell;
    },
    async setOrderProposalAsCart(orderProposal: OrderProposal) {
      this.loadingCart = true;
      this.cartId = orderProposal.basketId;
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      cartId.value = this.cartId;

      setTimeout(async() => {
        await this.getCart(false);
      }, 500);
    },
    setCartFromId(id: string) {
      if (!useRuntimeConfig().public.fetchStoredBasket) {
        return;
      }
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      cartId.value = id;
      this.cartId = id;

      setTimeout(async() => {
        this.getCart(false);
      }, 200);
    },
    setAddedToCartCount(payload: number) {
      this.addedToCartCount = payload;
    },
    /**
     *
     * @param createIfNotExists - creates a new cart if there's no cartId cookie
     */
    async getCart(createIfNotExists = true) {
      const globalContent = useGlobalContentStore();
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });

      // Always allow loading an existing cart (e.g., B2B non-logged-in users with samples)
      // Only check getPurchasesEnabled when creating a new cart
      if (!globalContent.getPurchasesEnabled && !cartId.value) {
        this.isLoading = false;
        return;
      }
      this.loadingCart = true;
      const { apiGet, apiPost } = useApiFetch();

      if (!cartId.value && !createIfNotExists) {
        this.loadingCart = false;
        return;
      }

      let failedLoad = false;
      if (cartId.value) {
        const existingCart = await apiGet<CartApiResponse>(
          `/basket/${cartId.value}`
        );
        // Status 3 = Basket (active), Status 7 = Ready for Payment (during checkout)
        const validBasketStatuses = [3, 7];
        if (existingCart?.id && validBasketStatuses.includes(existingCart?.statusId)) {
          if (useUserStore().isLoggedIn && !existingCart.customerId) {
            // Basket was created without authentication while user was logged in.
            // Discard it and create a new one so it gets bound to the customer's company.
            cartId.value = null;
            this.cartId = '';
            this.cart = emptyCart;
            failedLoad = true;
            createIfNotExists = true;
          } else {
            cartId.value = existingCart.id;
            this.cartId = existingCart.id;
            this.cart = existingCart;
            Sentry.setContext('basket', {
              uuid: existingCart.id,
            });
          }
        } else {
          cartId.value = null;
          this.cartId = '';
          this.cart = emptyCart;
          failedLoad = true;
        }
      }

      // creates a new cart if it doesn't exist
      if (!cartId.value || failedLoad) {
        const newCart = await apiPost<CartApiResponse>(
          '/basket'
        );
        if (newCart?.id) {
          cartId.value = newCart.id;
          this.cartId = newCart.id;
          this.cart = newCart;
          Sentry.setContext('basket', {
            uuid: newCart.id,
          });
        }
      }

      this.cartItems = this.cart.items.filter(customerSuccessItemFilter).map((i)=> i.quantity).reduce((a,b)=>a+b, 0);
      this.lastUpdate = Date.now();
      this.loadingCart = false;
    },
    async createNewCart() {
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      cartId.value = null;
      this.cartItems = 0;
      this.cart = emptyCart;
      this.cartId = '';
      this.lastUpdate = Date.now();
    },
    async getCheckout() {
      const { apiGet } = useApiFetch();
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      if (!cartId.value) {
        console.log('no cart id');
        return false;
      }
      this.loadingCheckout = true;
      const checkout = await apiGet<CheckoutApiResponse>(
        `/basket/${cartId.value}/checkout`, {}, false
      );
      if (checkout) {
        this.cart = checkout.basket;
        this.cartItems = this.cart.items.filter(customerSuccessItemFilter).map((i)=> i.quantity).reduce((a,b)=>a+b, 0);
        this.shippingAddress = checkout.shippingAddress;
        this.billingAddress = checkout.billingAddress;
        this.payerAddress = checkout.payerAddress;

        // Set companyName if it's not already set
        if (!this.shippingAddress.companyName && this.billingAddress.companyName) {
          this.shippingAddress.companyName = this.billingAddress.companyName;
        }
        this.deliveryMethods = checkout.deliveryMethods;
        this.paymentMethods = checkout.paymentMethods as CheckoutPaymentMethod[];
        this.buyerCompanyName = checkout.buyerCompanyName;
        this.loadingCart = false;
        this.loadingCheckout = false;
        this.checkoutVersion++;
        if (this.deliveryMethods.length && !this.deliveryMethods.some(m => m.selected)) {
          setTimeout(() => {
            const firstDeliveryMethod = this.deliveryMethods[0];
            if (firstDeliveryMethod) {
              this.updateDeliveryMethod(parseInt(firstDeliveryMethod.id, 10));
            }
          }, 100);
        }
        return true;
      }
      this.cartId = cartId.value;
      this.loadingCheckout = false;
      return false;
    },
    async updateDeliveryMethod(deliveryMethodId: number) {
      const { apiPost, handleApiError } = useApiFetch();
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      if (cartId.value !== null && cartId.value !== undefined) {
        this.cartId = cartId.value;
      } else {
        return false;
      }
      const checkout = await apiPost<CheckoutApiResponse>(
        `/basket/${cartId.value}/delivery-method`,
        {
          id: deliveryMethodId,
        }
      );
      if (checkout) {
        this.cart = checkout.basket;
        this.deliveryMethods = checkout.deliveryMethods;
        this.checkoutVersion++;
      } else {
        handleApiError();
      }
    },
    async updatePaymentMethod(paymentMethodId: number) {
      const { apiPost, handleApiError } = useApiFetch();
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      // [NC-DEBUG] Any payment-method write on the basket before initialize could set a
      // non-exportable method. Log who/what triggers it and on which basket.
      console.log('[NC-DEBUG] cartStore.updatePaymentMethod()', {
        paymentMethodId,
        basketId: cartId.value,
        caller: new Error().stack?.split('\n').slice(2, 5).join(' | '),
      });
      if (cartId.value !== null && cartId.value !== undefined) {
        this.cartId = cartId.value;
      } else {
        return false;
      }
      const res = await apiPost<CheckoutApiResponse>(
        `/basket/${cartId.value}/payment-method`,
        {
          id: paymentMethodId,
        }
      );
      if (res) {
        this.cart = res.basket;
        this.paymentMethods = res.paymentMethods;
        this.checkoutVersion++;
      } else {
        handleApiError();
      }
    },
    async updateCartInfoType(code: string, value: string) {
      const { apiPost, handleApiError } = useApiFetch();
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      if (cartId.value !== null && cartId.value !== undefined) {
        this.cartId = cartId.value;
      } else {
        return false;
      }
      const res = await apiPost<CartApiResponse>(
        `/basket/${cartId.value}/infotype`,
        {
          code,
          value,
        }
      );
      if (!res) {
        handleApiError();
      } else {
        this.cart = res;
        this.lastUpdate = Date.now();
        this.checkoutVersion++;
      }
    },
    async updateCartItemInfoType(item: CartApiItem, code: string, value: string) {
      const { apiPost, handleApiError } = useApiFetch();
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      if (cartId.value !== null && cartId.value !== undefined) {
        this.cartId = cartId.value;
      } else {
        return false;
      }
      const res = await apiPost<CartApiResponse>(
        `/basket/${cartId.value}/${item.lineNo}/infotype`,
        {
          code,
          value,
        }
      );
      if (!res) {
        handleApiError();
      } else {
        this.cart = res;
        this.checkoutVersion++;
      }
    },
    async mergeDeliveries() {
      const { apiPost, handleApiError } = useApiFetch();
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      if (cartId.value !== null && cartId.value !== undefined) {
        this.cartId = cartId.value;
      } else {
        return false;
      }
      const res = await apiPost<CheckoutApiResponse>(
        `/basket/${cartId.value}/merge-deliveries`
      );
      if (!res) {
        handleApiError();
      } else {
        await this.getCheckout();
      }
    },
    async updatePayer(payerId: number) {
      const { apiPost, handleApiError } = useApiFetch();
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      if (cartId.value !== null && cartId.value !== undefined) {
        this.cartId = cartId.value;
      } else {
        return false;
      }
      const res = await apiPost<CheckoutApiResponse>(
        `/basket/${cartId.value}/payer`,
        { id: payerId }
      );
      if (!res) {
        handleApiError();
      } else {
        this.cart = res.basket;
        this.payerAddress = res.payerAddress;
      }
    },
    async createAndSetPayer(firstName: string, lastName: string) {
      const { apiPost, handleApiError } = useApiFetch();
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      if (cartId.value !== null && cartId.value !== undefined) {
        this.cartId = cartId.value;
      } else {
        return false;
      }
      const res = await apiPost<CheckoutApiResponse>(
        `/basket/${cartId.value}/payer`,
        { firstName, lastName }
      );
      if (!res) {
        handleApiError();
        return false;
      } else {
        this.cart = res.basket;
        this.payerAddress = res.payerAddress;
        return true;
      }
    },
    async updateInvoiceNotice(invoiceNotice: string) {
      const { apiPost, handleApiError } = useApiFetch();
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      if (cartId.value !== null && cartId.value !== undefined) {
        this.cartId = cartId.value;
      } else {
        return false;
      }
      const res = await apiPost<CheckoutApiResponse>(
        `/basket/${cartId.value}/invoice-notice`,
        { invoiceNotice }
      );
      if (!res) {
        handleApiError();
      }
    },

    async updateComment(comment: string) {
      const { apiPost, handleApiError } = useApiFetch();
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      if (cartId.value !== null && cartId.value !== undefined) {
        this.cartId = cartId.value;
      } else {
        return false;
      }
      const res = await apiPost<CheckoutApiResponse>(
        `/basket/${cartId.value}/comment`,
        { comment }
      );
      if (!res) {
        handleApiError();
      }
    },

    async updateGlobalDiscount(discount: number) {
      const { apiPut, handleApiError } = useApiFetch();
      const cartId = useCookie('cartId', {
        maxAge: 3600 * 24 * 182,
        path: '/' + useRoute().path?.split('/').filter((str) => str.length).shift(),
      });
      if (cartId.value !== null && cartId.value !== undefined) {
        this.cartId = cartId.value;
      } else {
        return false;
      }
      const res = await apiPut(
        `/basket/quick/${cartId.value}/global-discount`,
        { discount }
      );
      if (!res) {
        handleApiError();
      }
    },

    async updateShippingAddress(shippingForm: CheckoutAddressDetails, toConsumer = false) {
      const { apiPost, handleApiError } = useApiFetch();
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      this.loadingCheckout = true;

      // Set companyName if it's not already set
      if (!this.shippingAddress.companyName && this.billingAddress.companyName) {
        this.shippingAddress.companyName = this.billingAddress.companyName;
      }

      const checkout = await apiPost<CheckoutApiResponse>(
        `/basket/${cartId.value}/delivery`,
        {
          companyName: shippingForm.companyName || this.shippingAddress.companyName,
          orgNo: this.shippingAddress.orgNo,
          email: shippingForm.email,
          // For "ship to consumer", use the name field exclusively (ignore hydrated firstName/lastName from buyer)
          firstName: toConsumer ? shippingForm.name : (shippingForm.firstName || shippingForm.name),
          lastName: toConsumer ? '' : (shippingForm.lastName ?? ''),
          toConsumer,
          phone: shippingForm.phone,
          deliveryAddress: {
            line1: (shippingForm.firstName && shippingForm.lastName)
              ? `${shippingForm.firstName} ${shippingForm.lastName}`.trim()
              : shippingForm.name || this.shippingAddress.companyName,
            line2: shippingForm.street,
            zip: shippingForm.zip,
            city: shippingForm.city,
            country: shippingForm.country,
            careOf: shippingForm.companyName,
            region: shippingForm.state,
          },
        }
      );
      if (checkout) {
        this.cart = checkout.basket;
        this.shippingAddress = checkout.shippingAddress;
        this.deliveryMethods = checkout.deliveryMethods;
        if (!this.deliveryMethods.some(m => m.selected) && this.deliveryMethods.length > 0) {
          setTimeout(() => {
            this.updateDeliveryMethod(parseInt(this.deliveryMethods[0].id, 10));
          }, 100);
        }
        this.loadingCheckout = false;
        return true;
      } else {
        handleApiError();
        this.isLoading = false;
        return false;
      }
    },
    async updateShippingAddressFromSalon(salonData?: UserProfileAddress) {
      const { apiPost, handleApiError } = useApiFetch();
      if (salonData) {
        const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
        this.loadingCheckout = true;
        const checkout = await apiPost<CheckoutApiResponse>(
          `/basket/${cartId.value}/delivery`,
          {
            email: this.shippingAddress.email,
            firstName: this.shippingAddress.firstName,
            lastName: this.shippingAddress.lastName,
            deliveryAddress: salonData,
          }
        );
        if (checkout) {
          if (!useRoute().path?.endsWith('fastOrder')) {
            this.cart = checkout.basket;
          }
          this.shippingAddress = checkout.shippingAddress;
          this.deliveryMethods = checkout.deliveryMethods;
          if (!this.deliveryMethods.some(m => m.selected)) {
            setTimeout(() => {
              this.updateDeliveryMethod(parseInt(this.deliveryMethods[0].id, 10));
            }, 100);
          }
        } else {
          handleApiError();
        }
        this.loadingCheckout = false;
      } else {
        handleApiError();
      }
    },

    async handleDiscountCode(discountCode: string, isDelete?: boolean) {
      const { apiPost, apiDelete } = useApiFetch();
      let res;
      if (isDelete) {
        res = await apiDelete<CheckoutApiResponse>(
          `/basket/${this.cartId}/discount-code`,
          {
            discountCode: discountCode,
          }
        );
      } else {
        res = await apiPost<CheckoutApiResponse>(
          `/basket/${this.cartId}/discount-code`,
          {
            discountCode: discountCode,
          }
        );
      }
      if (res) {
        this.isLoading = true;
        this.cart = res.basket;
        this.cartItems = this.cart.items.filter(customerSuccessItemFilter).map((i)=> i.quantity).reduce((a,b)=>a+b, 0);
        setTimeout(() => {
          this.isLoading = false;
        }, 500);
      }

      return res;
    },

    async saveOrderProposal(
      status: string,
      validUntil: number,
      comment: string,
      customerId?: number | null
    ) {
      const { apiPost, handleApiError } = useApiFetch();

      const res = await apiPost<UserProfile>('/order-proposals', {
        basketId: this.cartId,
        status,
        validUntil,
        comment,
        customerId,
      });

      if (res) {
        useUserStore().setUserProfile(res);
        return true;
      } else {
        handleApiError();
      }
    },

    async requestOrderProposal(
      message: string
    ) {
      if (!this.cartId && this.cart.id) {
        this.cartId = this.cart.id;
      }

      const { apiPost, handleApiError } = useApiFetch();

      const res = await apiPost<UserProfile>('/order-proposals/request', {
        basketId: this.cartId,
        userComment: message,
      });

      if (res) {
        useUserStore().setUserProfile(res);
        return true;
      } else {
        handleApiError();
      }
    },

    async updateProductDiscount(
      lineNo: number,
      discount: string,
      quantity: number,
      priceListLocked = false,
      discountType: 'percentage' | 'fixedPerM2' = 'percentage',
      shouldUpdateProduct = false,
      shouldUpdateExistingLineNo = false
    ) {
      return this.updateProductDiscountBatch([
        {
          lineNo,
          discount,
          quantity,
          priceListLocked,
          discountType,
          shouldUpdateProduct,
          shouldUpdateExistingLineNo,
        },
      ]);
    },

    async updateProductDiscountBatch(
      items: DiscountUpdateItem[],
      {
        shouldUpdateProduct: defaultShouldUpdateProduct = false,
        shouldUpdateExistingLineNo: defaultShouldUpdateExistingLineNo = false,
      }: { shouldUpdateProduct?: boolean; shouldUpdateExistingLineNo?: boolean } = {}
    ) {
      if (!items.length) {
        return null;
      }

      const { apiPost } = useApiFetch();

      const payloadItems = items.map((item) => ({
        lineNo: item.lineNo,
        discount: item.discount,
        quantity: item.quantity,
        priceListLocked: item.priceListLocked ?? false,
        discountType: item.discountType ?? 'percentage',
        shouldUpdateProduct: item.shouldUpdateProduct ?? defaultShouldUpdateProduct,
        shouldUpdateExistingLineNo: item.shouldUpdateExistingLineNo ?? defaultShouldUpdateExistingLineNo,
      }));

      const res = await apiPost<CartApiResponse>('/products/discount', {
        basketId: this.cartId,
        items: payloadItems,
      });

      if (res) {
        this.cart = res;
        this.cartItems = this.cart.items.filter(customerSuccessItemFilter).map((i)=> i.quantity).reduce((a,b)=>a+b, 0);

        if (this.cartId === '') {
          const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
          cartId.value = this.cart.id;
          this.cartId = this.cart.id;
        }
      }

      return res;
    },

    async updateNewsletterConfirm(checked: boolean) {
      const { apiPut, handleApiError } = useApiFetch();
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      const checkout = await apiPut<CheckoutApiResponse>(
        `/basket/${cartId.value}/newsletter`,
        {
          hasNewsletterSubscription: checked,
        }
      );
      if (checkout) {
        this.cart = checkout.basket;
      } else {
        handleApiError();
      }
    },

    async deleteCart() {
      const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
      cartId.value = null;
      this.cartItems = 0;
      this.cart = emptyCart;
      this.cartId = '';
      if (!useUserStore().isLoggedIn) {
        return;
      }
    },

    async completePurchase() {
      const uiStore = useUiStore();
      const userStore = useUserStore();
      const { apiPost, handleApiError, lastError, lastErrorCode } = useApiFetch();

      if (userStore.isMediabankUser) {
        return;
      }

      this.loadingPurchase = true;
      this.hasAttemptedPurchase = true;

      /**
       * Algolia tracking. Check if we have a query id connected to this product
       * For now, we use partNo, they should be interchangeable
       */
      const track: string[] = [];
      const trackWqueryId1: string[] = [];
      const trackWqueryId2: {queryID: string}[] = [];
      this.cart.items.forEach((item) => {
        const queryID = localStorage.getItem(`objectID-${item.partNo}`);
        if (queryID) {
          trackWqueryId1.push(item.partNo);
          trackWqueryId2.push({ queryID });
        } else {
          track.push(item.partNo);
        }
      });
      const browserTrackingCookie = useCookie('randomId', { maxAge: 3600 * 24 * 365 });
      const globalContent = useGlobalContentStore();
      if (trackWqueryId1.length) {
        aa('purchasedObjectIDsAfterSearch', {
          userToken: browserTrackingCookie.value || '',
          eventName: 'Tracked Product Purchase',
          index: globalContent.getAlgoliaIndex,
          objectIDs: trackWqueryId1,
          objectData: trackWqueryId2,
          queryID: '',
        });
      }
      if (track.length) {
        aa('purchasedObjectIDs', {
          userToken: browserTrackingCookie.value || '',
          eventName: 'Product Purchase',
          index: globalContent.getAlgoliaIndex,
          objectIDs: track,
        });
      }

      const res = await apiPost<PurchaseResponse>(`/basket/${this.cartId}/purchase`);
      const { manualUrl } = useLinkReplacer();

      if (res !== null) {
        if (res.allowPointsPurchase && res.delayedUntil) {
          await navigateTo(manualUrl(`/checkout/pointshop/${res.basketId}?delayedUntil=${encodeURIComponent(res.delayedUntil)}`));
        } else {
          await navigateTo(manualUrl(`/checkout/success/${res.basketId}`));
        }

      } else {
        this.loadingPurchase = false;
        if (lastErrorCode.value === 422) {
          if (lastError.value?.message) {
            uiStore.setTemporaryError(lastError.value.message);
          } else {
            const { $t } = useNuxtApp();
            uiStore.setTemporaryError($t('apiError.unknown'));
          }
        } else {
          handleApiError();
        }
      }

      uiStore.setShowValidateCheckoutModal(false);
      this.loadingPurchase = false;
    },

    /**
     * Adds or updates cart
     * @param partNo
     * @param quantity - set zero to remove
     * @param lineNo - if updating cart, otherwise -1
     * @param openAdded - if "added to cart" modal should open
     * @param product - we need the product for tracking
     * @param limitedProduct - if we don't have entire product data
     * that makes it harder. If we could refactor cart items to compatible with product,
     * this would work better, but it's a big refactor
     * @param objectID - objectID from algolia, if we have it, used for tracking
     * @param priceListLocked
     * @param toggleCrossDocking
    */
    async updateCart(
      partNo: string,
      quantity = 1,
      lineNo = -1,
      openAdded = false,
      product: CartApiItem | ProductVariantModel | OrderProduct | null = null,
      limitedProduct = false,
      objectID: string = '',
      priceListLocked = false,
      toggleCrossDocking = false
    ) {
      const uiStore = useUiStore();
      const userStore = useUserStore();
      const route = useRoute();
      this.isLoading = true;
      const modifiedQuantity = quantity;
      const { apiPost, lastError } = useApiFetch();

      // Error message if sales rep / customer success user is not impersonating
      if ((userStore.isCustomerSuccessUser || userStore.isSalesRepUser) && !userStore.userProfile.impersonatingCompany && !this.cartId) {
        const { $t } = useNuxtApp();
        uiStore.setTemporaryError($t('user.impersonateToAddToCart'));
        return true;
      }

      // Mediabank users are not allowed to purchase
      if (userStore.isMediabankUser) {
        return true;
      }

      // when adding, check if already in cart.
      if (lineNo === -1) {
        const infoTypes = [
          NorceInfoTypesCodes.SalesRepDiscount,
          NorceInfoTypesCodes.CrossDocking,
          /**
           * @important - If you need to check something in the DataLayer, you can't just see if it exists at all,
           * you need to check individual keys inside the datalayer
           */
          //NorceInfoTypesCodes.DataLayer,
          NorceInfoTypesCodes.PossibleSalesRepGift,
        ] as string[];

        const findLineNo = this.cart.items.find((cartItem) => {
          if (cartItem.partNo !== partNo) {
            return false;
          }
          if (cartItem.priceListLocked !== priceListLocked) {
            return false;
          }
          if (cartItem.parentLineNo) {
            return false;
          }

          // Checking infotypes - values as strings.
          // If you change this or add more infotypes, please, please comment why, it's impossible to figure out from the code
          cartItem.infoTypes.filter(f => infoTypes.includes(f.name)).forEach((infoType) => {
            if (infoType.value !== 'false') {
              return false;
            }
          });

          return true;
        });

        if (findLineNo) {
          lineNo = findLineNo.lineNo;
          if (quantity === -1) {
            quantity = findLineNo.quantity - 1;
          } else if (quantity > 0) {
            quantity = findLineNo.quantity + quantity;
          }
        }
      }

      const payload = {
        items: [
          {
            partNo,
            quantity,
            priceListLocked,
          },
        ],
      } as CartApiPayload;

      if (lineNo > -1) {
        payload.items[0].lineNo = lineNo;
      }

      let beforeChange = null;
      if (lineNo >= 0) {
        beforeChange = this.cart.items.find((f) => f.lineNo === lineNo);
      }

      if (this.isBuyingUpsellProduct) {
        payload.items[0].isCrossSell = this.isBuyingUpsellProduct;
      }

      if (toggleCrossDocking) {
        const currentValue = this.cart.items
          .find((f) => f.lineNo === lineNo)
          ?.infoTypes
          ?.filter((i) => i.name === NorceInfoTypesCodes.CrossDocking)[0];
        payload.items[0].info = [
          { code: NorceInfoTypesCodes.CrossDocking, value: currentValue?.value == 'true' ? '0' : '1' },
        ];
      }

      const update = await apiPost<CartApiResponse>(
        `/basket/items/${this.cartId}`,
        payload
      );
      if (update) {
        this.cart = update;
        this.cartItems = this.cart.items.filter(customerSuccessItemFilter).map((i)=> i.quantity).reduce((a,b)=>a+b, 0);

        if (useRoute().path.includes('/checkout')) {
          await this.toggleModFee();
          await this.getCheckout();
        }
        if (update.messages) {
          update.messages.forEach((f) => {
            uiStore.setTemporaryError(f.split(':')[1]);
          });
        }
        if (product) {
          let sendQuantity = quantity;
          if (beforeChange) {
            sendQuantity = quantity - beforeChange.quantity;
          }
          let trackEvent = 'add_to_cart';
          if (sendQuantity < 0) {
            sendQuantity = Math.abs(sendQuantity);
            trackEvent = 'remove_from_cart';
          }

          sendDataLayer(
            trackEvent,
            [ createDataLayerItem(product, sendQuantity)]
          );
        }
        // Algolia track
        if (objectID !== '') {
          const browserTrackingCookie = useCookie('randomId', { maxAge: 3600 * 24 * 365 });
          const globalContent = useGlobalContentStore();
          const queryID = localStorage.getItem('queryID') || '';
          const insightPayload = {
            userToken: browserTrackingCookie.value,
            eventName: 'Product Added To Cart',
            objectIDs: [ objectID ],
            index: globalContent.getAlgoliaIndex,
          } as any;
          if (queryID !== '') {
            insightPayload.objectData = [
              {
                queryID,
              },
            ];
          }

          // adds to local storage, to track after purchase
          localStorage.setItem(`objectId-${objectID}`, queryID);
          if (queryID !== '') {
            aa('addedToCartObjectIDsAfterSearch', insightPayload);
          } else {
            aa('addedToCartObjectIDs', insightPayload);
          }
        } else {
          // console.log('no object id');
        }

        if (this.cartId === '') {
          const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
          cartId.value = this.cart.id;
          this.cartId = this.cart.id;
        }

        this.isLoading = false;

        if (uiStore.currentlyEditingItem) {
          const item = this.cart.items.find((item) => item.lineNo === uiStore.currentlyEditingItem?.lineNo);
          if (item) {
            uiStore.setCurrentlyEditingItem(item);
          }
        }

        // Product added
        if (quantity >= 1) {
          this.justAddedPartNo = partNo;
        }

        if (openAdded && quantity >= 1) {
          if (product && !limitedProduct) {
            // @ts-ignore
            this.currentlyBuying = product;
            this.currentlyBuyingQuantity = modifiedQuantity;
          }
          const shouldShowAddedToCart = route.name !== 'checkout';
          uiStore.setShowAddedToCart(shouldShowAddedToCart);
          uiStore.setShowQuickBuy(false);
        }
      } else {
        // Failed to update cart, most likely because not enough in stock
        const { $t } = useNuxtApp();
        uiStore.setTemporaryError(lastError?.value?.message ?? $t('addedToCart.fail'));
        // reloads the cart so we know it's ok
        await this.getCart(false);
        this.isLoading = false;
      }
      if (this.isBuyingUpsellProduct) {
        this.isBuyingUpsellProduct = false;
      }
      return true;
    },

    async toggleModFee() {
      if (import.meta.server) {
        return;
      }

      if (this.toggleModFeeInProgress) {
        return;
      }

      const globalContent = useGlobalContentStore();
      if (!globalContent.getEnableBreakModeOfDelivery && !globalContent.consumerFeePartNo) {
        return;
      }

      this.toggleModFeeInProgress = true;

      try {
        const consumerFeePartNo = globalContent.consumerFeePartNo;
        const outsideModFeePartNo = globalContent.outsideModDeliveryFeePartNo;

        // Remove all items if sales or CS has disabled fees for outside deliveries or delivery type is address
        if (this.hasDisabledOutsideModFee || this.deliveryToAddress) {
          const items = [];
          if (this.hasConsumerFeeProduct && consumerFeePartNo) {
            items.push({
              partNo: consumerFeePartNo,
              quantity: 0,
            });
          }
          if (this.hasOutsideModProduct && outsideModFeePartNo) {
            items.push({
              partNo: outsideModFeePartNo,
              quantity: 0,
            });
          }
          if (items.length > 0) {
            await this.addMultipleItemsToCart(items, false, true);
          }
          return;
        }
        // Delivery to registered address should remove consumer fee and add outside mod fee
        else if (this.deliveryToStore) {
          const items = [];
          if (this.hasConsumerFeeProduct && consumerFeePartNo) {
            items.push({
              partNo: consumerFeePartNo,
              quantity: 0,
            });
          }
          if (this.hasItemInCartOutsideMod && !this.hasOutsideModProduct && outsideModFeePartNo) {
            items.push({
              partNo: outsideModFeePartNo,
              quantity: 1,
            });
          } else if (!this.hasItemInCartOutsideMod && this.hasOutsideModProduct && outsideModFeePartNo) {
            items.push({
              partNo: outsideModFeePartNo,
              quantity: 0,
            });
          }
          if (items.length > 0) {
            await this.addMultipleItemsToCart(items, false, true);
          }
          return;
        }
        // Delivery to consumer should add consumer fee and remove outside mod fee
        else if (this.deliveryToConsumer) {
          const items = [];

          // Skip consumer fee if cart has only samples AND market is SE
          const shouldSkipConsumerFee = globalContent.getFreeConsumerShippingIfSamples && this.hasOnlySamples;

          if (!this.hasConsumerFeeProduct && !shouldSkipConsumerFee && consumerFeePartNo) {
            // No fee in cart and should charge
            items.push({
              partNo: consumerFeePartNo,
              quantity: 1,
            });
          } else if (this.hasConsumerFeeProduct && shouldSkipConsumerFee && consumerFeePartNo) {
            // Fee exists and should be free
            items.push({
              partNo: consumerFeePartNo,
              quantity: 0,
            });
          }

          if (this.hasOutsideModProduct && outsideModFeePartNo) {
            items.push({
              partNo: outsideModFeePartNo,
              quantity: 0,
            });
          }
          if (items.length > 0) {
            await this.addMultipleItemsToCart(items, false, true);
          }
          return;
        }
      } finally {
        this.toggleModFeeInProgress = false;
      }
    },
    /**
     *
     * @param items
     * @param openAdded
     * @param overwrite - if true, quantities already in cart are overwritten, else changed
     */
    async addMultipleItemsToCart(items: multiAddToCart[], openAdded: boolean, overwrite = false) {
      const uiStore = useUiStore();
      const userStore = useUserStore();
      const route = useRoute();

      if (userStore.isMediabankUser) {
        return;
      }

      this.isLoading = true;
      const { apiPost, lastError } = useApiFetch();
      // when adding, check if already in cart.
      const payloadItems = items.map(item => {
        const findLineNo = this.cart.items.find((f) => f.partNo === item.partNo && !f.priceListLocked && !f.parentLineNo);
        let newQuantity = item.quantity;

        if (findLineNo && !overwrite) {
          if (newQuantity === -1) {
            newQuantity = findLineNo.quantity - 1;
          } else if (newQuantity > 0) {
            newQuantity = findLineNo.quantity + newQuantity;
          }
        }
        return {
          ...item,
          quantity: newQuantity,
          lineNo: findLineNo?.lineNo ?? -1,
        };
      });

      const payload = {
        items: payloadItems.map(item => ({ partNo: item.partNo, quantity: item.quantity, lineNo: item.lineNo })),
      };

      const update = await apiPost<CartApiResponse>(
        `/basket/items/${this.cartId}`,
        payload
      );
      if (update) {
        if (useRoute().path.includes('/checkout')) {
          await this.getCheckout();
        }
        payloadItems.forEach(item => {
          if (item.product) {
            let sendQuantity = item.quantity;
            let beforeChange = null;
            if (item.lineNo >= 0) {
              beforeChange = this.cart.items.find((f) => f.lineNo === item.lineNo);
            }
            if (beforeChange) {
              sendQuantity = item.quantity - beforeChange.quantity;
            }
            let trackEvent = 'add_to_cart';
            if (sendQuantity < 0) {
              sendQuantity = Math.abs(sendQuantity);
              trackEvent = 'remove_from_cart';
            }

            sendDataLayer(
              trackEvent,
              [ createDataLayerItem(item.product, sendQuantity)]
            );
          }
        });

        this.cart = update;

        this.cartItems = this.cart.items.filter(customerSuccessItemFilter).map((i)=> i.quantity).reduce((a,b)=>a+b, 0);

        if (this.cartId === '') {
          const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
          cartId.value = this.cart.id;
          this.cartId = this.cart.id;
        }

        this.isLoading = false;

        if (uiStore.currentlyEditingItem) {
          const item = this.cart.items.find((item) => item.lineNo === uiStore.currentlyEditingItem?.lineNo);
          if (item) {
            uiStore.setCurrentlyEditingItem(item);
          }
        }

        if (openAdded && payloadItems.length === 1) {
          if (items[0].product) {
            // @ts-ignore
            this.currentlyBuying = items[0].product;
            this.currentlyBuyingQuantity = items[0].quantity;
          }
          const shouldShowAddedToCart = route.name !== 'checkout';
          uiStore.setShowAddedToCart(shouldShowAddedToCart);
          uiStore.setShowQuickBuy(false);
        }
      } else {
        // Failed to update cart, most likely because not enough in stock
        const { $t } = useNuxtApp();
        uiStore.setTemporaryError(lastError?.value?.message ?? $t('addedToCart.fail'));
        // reloads the cart so we know it's ok
        await this.getCart(false);
      }
      if (this.isBuyingUpsellProduct) {
        this.isBuyingUpsellProduct = false;
      }
      return true;
    },
    /**
     * This method is similar to addMultipleItemsToCart, but you have to deal with lineNo:s manually
     * It's used for the "fast order" funcionality
     */
    async updateMultipleItemsWithLineNo({ items, shouldMerge }: { items: UpdateWithLineNo[], shouldMerge: boolean }) {
      const { apiPost, handleApiError } = useApiFetch();
      this.isLoading = true;
      const endpoint = shouldMerge ? `/basket/quick/${this.cartId}` : '/basket/quick';

      const res = await apiPost<CartApiResponse>(
        endpoint,
        { items }
      );
      if (res) {
        this.cart = res;

        this.cartItems = this.cart.items.filter(customerSuccessItemFilter).map((i)=> i.quantity).reduce((a,b)=>a+b, 0);

        if (this.cartId === '' || !shouldMerge) {
          const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
          cartId.value = this.cart.id;
          this.cartId = this.cart.id;
        }

        this.isLoading = false;
        if (res.messages) {
          return res.messages;
        }
      } else {
        handleApiError();
        this.isLoading = false;
      }
      return [];
    },

    async copyItemWithLineNo(lineNo: number) {
      if (!this.cartId) {
        return [];
      }

      const { apiPost, handleApiError } = useApiFetch();
      const res = await apiPost<CartApiResponse>(
        `/basket/${this.cartId}/${lineNo}/copy`,
        {}
      );

      if (res) {
        this.cart = res;
        this.cartItems = this.cart.items.filter(customerSuccessItemFilter).map((i)=> i.quantity).reduce((a,b)=>a+b, 0);

        if (this.cartId === '') {
          const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
          cartId.value = this.cart.id;
          this.cartId = this.cart.id;
        }

        return res.messages ?? [];
      }

      handleApiError();
      return [];
    },

    async updateCustomerSuccessProduct(payload: { partNo: string; message: string }) {
      const { apiPost, handleApiError } = useApiFetch();
      const res = await apiPost<CartApiResponse>(`/customer-success/basket/${this.cartId}/items`, {
        partNo: payload.partNo,
        message: payload.message,
      });

      if (res) {
        this.cart = res;
      } else {
        handleApiError();
      }
    },
    async updateCustomerSuccessInfoTypes(payload: { code: string; checked: boolean }) {
      const { apiPut, handleApiError } = useApiFetch();
      const res = await apiPut<CartApiResponse>(`/customer-success/basket/${this.cartId}`, {
        code: payload.code,
        value: payload.checked,
      });

      if (res) {
        this.cart = res;
        return true;
      } else {
        handleApiError();
        return false;
      }
    },
    async acceptOrderProposal(orderProposal: OrderProposal, mergeActiveBasket: boolean) {
      const { apiPost, handleApiError } = useApiFetch();
      const res = await apiPost<CartApiResponse>('/order-proposals/accept', {
        basketId: orderProposal.basketId,
        mergeActiveBasket: mergeActiveBasket ? this.cart.id : null,
      });
      if (res) {
        const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
        cartId.value = res.id;
        this.cartId = res.id;
        this.cart = res;
        this.cartItems = this.cart.items.filter(customerSuccessItemFilter).map((i)=> i.quantity).reduce((a,b)=>a+b, 0);
        return true;
      } else {
        handleApiError();
      }
    },
    async fetchBasketCopyByOrderId(orderNo: string) {
      const { apiPost, handleApiError } = useApiFetch();
      const res = await apiPost<CartApiResponse>(`/orders/${orderNo}/edit`);
      if (res) {
        const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
        cartId.value = res.id;
        this.cartId = res.id;
        this.cart = res;
        this.cartItems = this.cart.items.filter(customerSuccessItemFilter).map((i)=> i.quantity).reduce((a,b)=>a+b, 0);
        return true;
      } else {
        handleApiError();
      }
    },
    async addPreviewBasketToCart() {
      const uiStore = useUiStore();
      const userStore = useUserStore();
      if (uiStore.currentlyPreviewing?.cart) {
        let error = false;

        if ((userStore.isCustomerSuccessUser || userStore.isSalesRepUser) && userStore.userProfile.company.id !== uiStore.currentlyPreviewing.savedCart.companyId) {
          const { handleApiError } = useApiFetch();
          const companyId = uiStore.currentlyPreviewing.savedCart?.companyId;
          if (companyId === undefined || companyId === null) {
            error = true;
            handleApiError();
          } else {
            const res = await userStore.impersonateCompany(companyId.toString());
            if (!res) {
              error = true;
              handleApiError();
            }
          }
        }

        if (!error) {
          const cartId = useCookie('cartId', { maxAge: 3600 * 24 * 182, path: '/' });
          cartId.value = uiStore.currentlyPreviewing.cart.id;
          this.cartId = uiStore.currentlyPreviewing.cart.id;
          this.cart = uiStore.currentlyPreviewing.cart;
          this.cartItems = this.cart.items.filter(customerSuccessItemFilter).map((i)=> i.quantity).reduce((a,b)=>a+b, 0);
          return true;
        }
      }

      return false;
    },
    changeDebouncedQueue(product: multiAddToCart) {
      const exist = this.deboucedAddToCart.findIndex((f)=> f.partNo === product.partNo);
      if (exist >= 0) {
        this.deboucedAddToCart[exist].quantity = product.quantity;
      } else {
        this.deboucedAddToCart.push(product);
      }
      if (typeof this.deboucedTimer === 'number') {
        clearTimeout(this.deboucedTimer);
      }
      this.deboucedTimer = setTimeout(()=> {
        this.sendDebouncedQueue();
      }, 3000);
    },
    sendDebouncedQueue() {
      if (typeof this.deboucedTimer === 'number') {
        clearTimeout(this.deboucedTimer);
      }
      if (this.deboucedAddToCart.length > 0) {
        this.addMultipleItemsToCart(this.deboucedAddToCart, false, true);
        this.deboucedAddToCart = [];
      }
    },
  },
});
